import { useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'

/**
 * Kundli Studio history (admin-v2 /kundlis). Only the birth INPUTS are kept —
 * a saved kundli is re-cast from VedAstro when it is opened, with the exact
 * coordinates picked at the time so the chart comes back identical.
 *
 * Signed-in users only; for guests every call is skipped and `signedIn` is
 * false so the studio can show a sign-in hint instead.
 */

const KEY = ['kundli-studio', 'saved']
const LIST_LIMIT = 50

/** Saved row → the raw birth fields `KundliStudio`'s generate() takes. */
export function savedToBirth(row) {
  const g = row.gender || ''
  return {
    name: row.name || '',
    gender: g ? g.charAt(0).toUpperCase() + g.slice(1) : '',
    dob: row.birth_date,
    tob: row.birth_time,
    pob: row.place_name,
    location: { Name: row.place_name, Latitude: row.latitude, Longitude: row.longitude },
  }
}

/** Same identity the backend dedupes on — highlights the open chart in the list. */
export function savedKey({ dob, tob, lat, lon, name }) {
  return `${dob}|${tob}|${Number(lat).toFixed(4)}|${Number(lon).toFixed(4)}|${(name || '').trim()}`
}

/**
 * Studio birth fields → the API's saved-kundli fields, or null without a
 * picked place (coordinates are what make the re-cast identical).
 */
function birthFields(birth) {
  const loc = birth?.location
  if (!loc || !Number.isFinite(Number(loc.Latitude)) || !Number.isFinite(Number(loc.Longitude))) return null
  return {
    name: (birth.name || '').trim(),
    gender: birth.gender ? String(birth.gender).toLowerCase() : '',
    birth_date: birth.dob,
    birth_time: birth.tob,
    place_name: loc.Name || birth.pob || 'Unknown place',
    latitude: Number(loc.Latitude),
    longitude: Number(loc.Longitude),
  }
}

/** Drop blank filters so `{ search: '' }` and `{}` share one cache entry and URL. */
function cleanFilters(filters) {
  return Object.fromEntries(
    Object.entries(filters || {})
      .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      .filter(([, v]) => v !== '' && v != null),
  )
}

/**
 * `filters` are the GET /kundlis query params — search, gender, source,
 * birth_date_from / birth_date_to, sort, order. Pages load on demand via
 * `loadMore`, so a long history never arrives in one response.
 */
export function useSavedKundlis(filters = {}) {
  const { user, status, login } = useAuth()
  const signedIn = status === 'authenticated' && Boolean(user?.id)
  const queryClient = useQueryClient()
  const params = cleanFilters(filters)

  const query = useInfiniteQuery({
    queryKey: [...KEY, user?.id, params],
    queryFn: ({ pageParam }) => api.kundlis.list({ ...params, limit: LIST_LIMIT, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const meta = last?.meta
      return meta && meta.current_page < meta.last_page ? meta.current_page + 1 : undefined
    },
    enabled: signedIn,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...KEY, user?.id] })

  const saveMutation = useMutation({
    mutationFn: (payload) => api.kundlis.save(payload),
    onSuccess: invalidate,
  })

  const removeMutation = useMutation({
    mutationFn: (id) => api.kundlis.remove(id),
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.kundlis.update(id, payload),
    onSuccess: invalidate,
  })

  /**
   * Keep a freshly cast chart. Fire-and-forget: history is a convenience, so
   * a failed save never interrupts the studio — it is only logged.
   */
  const saveBirth = (birth, { source, bookingId } = {}) => {
    if (!signedIn) return
    const fields = birthFields(birth)
    if (!fields) return
    saveMutation.mutate(
      {
        ...fields,
        ...(source ? { source } : {}),
        ...(bookingId ? { booking_id: bookingId } : {}),
      },
      { onError: (err) => console.warn('[kundli-studio] could not save kundli', err) },
    )
  }

  /**
   * Correct a saved kundli — birth details from the studio form's shape plus
   * label/notes. Rejects (ApiRequestError, status 409 for a duplicate) so the
   * editor can say what went wrong.
   */
  const updateBirth = (id, birth, { label = '', notes = '' } = {}) => {
    const fields = birthFields(birth)
    if (!fields) return Promise.reject(new Error('A birth place with coordinates is required.'))
    return updateMutation.mutateAsync({ id, payload: { ...fields, label: label.trim(), notes: notes.trim() } })
  }

  return {
    signedIn,
    authLoading: status === 'loading',
    login,
    kundlis: query.data?.pages.flatMap((p) => p?.data ?? []) ?? [],
    total: query.data?.pages[0]?.meta?.total ?? 0,
    isLoading: signedIn && query.isPending,
    isFetching: query.isFetching && !query.isFetchingNextPage,
    isError: query.isError,
    refetch: query.refetch,
    hasMore: Boolean(query.hasNextPage),
    loadMore: () => query.fetchNextPage(),
    loadingMore: query.isFetchingNextPage,
    saveBirth,
    updateBirth,
    updatingId: updateMutation.isPending ? updateMutation.variables?.id : null,
    remove: (id) => removeMutation.mutateAsync(id),
    removingId: removeMutation.isPending ? removeMutation.variables : null,
  }
}
